import { useEffect } from 'react'
import { Controller, useForm, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { contentSchema, type ContentInput } from '@/lib/validations'
import { CANAIS, ETAPAS, FORMATOS, FRENTES, PRIORIDADES, STATUSES } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { useCreateTask, useUpdateTask } from '@/hooks/use-tasks'
import { useMembers } from '@/hooks/use-members'
import { useCampaigns } from '@/hooks/use-campaigns'
import { useEvents } from '@/hooks/use-events'
import type { TablesInsert } from '@/types/database'
import type { TaskWithRelations } from '@/types/domain'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/ui/field'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FrenteDot } from '@/components/common/badges'
import { ETAPA_TO_STATUS } from './production'

type FormState =
  | { mode: 'create'; defaults?: Partial<TablesInsert<'tasks'>> }
  | { mode: 'edit'; task: TaskWithRelations }
  | null

const NONE = '__none__'

function tipoFromFormato(formato?: string | null) {
  const f = (formato || '').toLowerCase()
  if (f.includes('carrossel')) return 'carrossel' as const
  if (f.includes('vídeo') || f.includes('video') || f.includes('reel')) return 'video' as const
  if (f.includes('imagem') || f.includes('story')) return 'post_estatico' as const
  return 'organico' as const
}

function defaults(state: FormState): Partial<ContentInput> {
  if (!state || state.mode === 'create') {
    const d = (state?.mode === 'create' ? state.defaults : undefined) ?? {}
    return {
      is_content: d.is_content ?? true,
      title: '',
      frente: (d.frente as ContentInput['frente']) ?? 'GERAL',
      prioridade: 'planejada',
      canais: [],
      etapa: 'ideia',
      status: 'entrada',
    }
  }
  const t = state.task
  return {
    is_content: t.is_content,
    title: t.title,
    frente: t.frente,
    prioridade: t.prioridade,
    status: t.status,
    etapa: t.etapa ?? 'ideia',
    formato: t.format ?? '',
    canais: t.canais ?? [],
    apresentador: t.apresentador ?? '',
    revisor: t.revisor ?? '',
    campaign_id: t.campaign_id ?? undefined,
    event_id: t.event_id ?? undefined,
    assignee_id: t.assignee_id ?? undefined,
    data_roteiro: t.data_roteiro ?? '',
    data_gravacao: t.data_gravacao ?? '',
    data_postagem: t.data_postagem ?? '',
    horario: t.horario ? t.horario.slice(0, 5) : '',
    next_action: t.next_action ?? '',
    description: t.description ?? '',
  }
}

function toPayload(v: ContentInput): Partial<TablesInsert<'tasks'>> {
  const n = (s?: string | null) => (s && s.length ? s : null)
  const base: Partial<TablesInsert<'tasks'>> = {
    is_content: v.is_content,
    title: v.title,
    description: n(v.description),
    frente: v.frente,
    prioridade: v.prioridade,
    assignee_id: v.assignee_id ?? null,
  }
  if (v.is_content) {
    const etapa = v.etapa ?? 'ideia'
    return {
      ...base,
      tipo: tipoFromFormato(v.formato),
      etapa,
      status: ETAPA_TO_STATUS[etapa],
      format: n(v.formato),
      canais: v.canais ?? [],
      apresentador: n(v.apresentador),
      revisor: n(v.revisor),
      campaign_id: v.campaign_id ?? null,
      event_id: v.event_id ?? null,
      data_roteiro: n(v.data_roteiro),
      data_gravacao: n(v.data_gravacao),
      data_postagem: n(v.data_postagem),
      horario: n(v.horario),
      start_date: n(v.data_roteiro),
      agreed_due_date: n(v.data_postagem),
      publish_date: v.data_postagem
        ? `${v.data_postagem}T${v.horario && /^\d/.test(v.horario) ? v.horario : '12:00'}:00-03:00`
        : null,
      next_action: n(v.next_action),
    }
  }
  return {
    ...base,
    tipo: 'planejamento',
    status: v.status ?? 'entrada',
    agreed_due_date: n(v.data_postagem),
    data_postagem: n(v.data_postagem),
  }
}

export function TaskFormDialog({ state, onClose }: { state: FormState; onClose: () => void }) {
  const open = state !== null
  const isEdit = state?.mode === 'edit'
  const createTask = useCreateTask()
  const updateTask = useUpdateTask()
  const { data: members = [] } = useMembers()
  const { data: campaigns = [] } = useCampaigns()
  const { data: events = [] } = useEvents()

  const form = useForm<ContentInput>({
    resolver: zodResolver(contentSchema),
    defaultValues: {
      is_content: true,
      title: '',
      frente: 'GERAL',
      prioridade: 'planejada',
      canais: [],
    },
  })
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = form

  useEffect(() => {
    if (open) reset(defaults(state) as ContentInput)
  }, [open, state, reset])

  const isContent = watch('is_content')

  async function onSubmit(values: ContentInput) {
    try {
      const payload = toPayload(values)
      if (state?.mode === 'edit') {
        await updateTask.mutateAsync({ id: state.task.id, patch: payload })
        toast.success(values.is_content ? 'Conteúdo atualizado' : 'Tarefa atualizada')
      } else {
        await createTask.mutateAsync(
          payload as Omit<TablesInsert<'tasks'>, 'workspace_id' | 'created_by'>,
        )
        toast.success(values.is_content ? 'Conteúdo criado' : 'Tarefa criada')
      }
      onClose()
    } catch (err) {
      toast.error('Não foi possível salvar', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar' : 'Novo'} {isContent ? 'conteúdo' : 'tarefa'}
          </DialogTitle>
        </DialogHeader>

        {!isEdit && (
          <Controller
            control={control}
            name="is_content"
            render={({ field }) => (
              <div className="inline-flex rounded-xl bg-muted/60 p-1 text-sm">
                <button
                  type="button"
                  onClick={() => field.onChange(true)}
                  className={cn('rounded-lg px-3 py-1', field.value && 'bg-background shadow-soft')}
                >
                  Conteúdo
                </button>
                <button
                  type="button"
                  onClick={() => field.onChange(false)}
                  className={cn(
                    'rounded-lg px-3 py-1',
                    !field.value && 'bg-background shadow-soft',
                  )}
                >
                  Tarefa livre
                </button>
              </div>
            )}
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Field label="Título" required error={errors.title?.message}>
            <Input
              placeholder={
                isContent ? 'Ex.: Reel — 3 erros no scanner' : 'Ex.: Confirmar pauta com o Diego'
              }
              {...register('title')}
            />
          </Field>

          {isContent ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <FrenteSelect control={control} />
                <SimpleSelect
                  control={control}
                  name="formato"
                  label="Formato"
                  options={FORMATOS.map((f) => ({ value: f, label: f }))}
                  clearable
                />
                <SimpleSelect
                  control={control}
                  name="etapa"
                  label="Etapa"
                  options={ETAPAS.map((e) => ({ value: e.value, label: e.label }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <RelSelect
                  control={control}
                  name="campaign_id"
                  label="Campanha"
                  items={campaigns.map((c) => ({ id: c.id, name: c.name }))}
                />
                <RelSelect
                  control={control}
                  name="event_id"
                  label="Evento"
                  items={events.map((e) => ({ id: e.id, name: e.name }))}
                />
                <SimpleSelect
                  control={control}
                  name="prioridade"
                  label="Prioridade"
                  options={PRIORIDADES.map((x) => ({ value: x.value, label: x.label }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <MemberSelect
                  control={control}
                  name="assignee_id"
                  label="Responsável"
                  members={members}
                />
                <Field label="Apresentador">
                  <Input {...register('apresentador')} />
                </Field>
                <Field label="Revisor">
                  <Input {...register('revisor')} />
                </Field>
              </div>

              <Controller
                control={control}
                name="canais"
                render={({ field }) => (
                  <Field label="Canais">
                    <div className="flex flex-wrap gap-1.5">
                      {CANAIS.map((c) => {
                        const on = (field.value ?? []).includes(c)
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() =>
                              field.onChange(
                                on
                                  ? field.value.filter((x) => x !== c)
                                  : [...(field.value ?? []), c],
                              )
                            }
                            className={cn(
                              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                              on
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border text-muted-foreground',
                            )}
                          >
                            {c}
                          </button>
                        )
                      })}
                    </div>
                  </Field>
                )}
              />

              <div className="rounded-xl border border-border/70 p-3">
                <p className="mb-2 text-sm font-medium">Datas de produção</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label="Roteiro">
                    <Input type="date" {...register('data_roteiro')} />
                  </Field>
                  <Field label="Gravação">
                    <Input type="date" {...register('data_gravacao')} />
                  </Field>
                  <Field label="Postagem">
                    <Input type="date" {...register('data_postagem')} />
                  </Field>
                  <Field label="Horário">
                    <Input type="time" {...register('horario')} />
                  </Field>
                </div>
              </div>

              <Field label="Próxima ação">
                <Input placeholder="O próximo passo concreto" {...register('next_action')} />
              </Field>
              <Field label="Roteiro / observações">
                <Textarea rows={3} {...register('description')} />
              </Field>
            </>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <FrenteSelect control={control} />
                <SimpleSelect
                  control={control}
                  name="prioridade"
                  label="Prioridade"
                  options={PRIORIDADES.map((x) => ({ value: x.value, label: x.label }))}
                />
                <SimpleSelect
                  control={control}
                  name="status"
                  label="Status"
                  options={STATUSES.map((x) => ({ value: x.value, label: x.label }))}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <MemberSelect
                  control={control}
                  name="assignee_id"
                  label="Responsável"
                  members={members}
                />
                <Field label="Prazo">
                  <Input type="date" {...register('data_postagem')} />
                </Field>
              </div>
              <Field label="Descrição">
                <Textarea rows={3} {...register('description')} />
              </Field>
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// --- sub-componentes --------------------------------------------------------
function FrenteSelect({ control }: { control: Control<ContentInput> }) {
  return (
    <Field label="Ambiente">
      <Controller
        control={control}
        name="frente"
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FRENTES.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  <span className="flex items-center gap-2">
                    <FrenteDot frente={f.value} /> {f.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </Field>
  )
}

function SimpleSelect({
  control,
  name,
  label,
  options,
  clearable,
}: {
  control: Control<ContentInput>
  name: 'formato' | 'prioridade' | 'status' | 'etapa'
  label: string
  options: { value: string; label: string }[]
  clearable?: boolean
}) {
  return (
    <Field label={label}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            value={(field.value as string) || (clearable ? NONE : undefined)}
            onValueChange={(v) => field.onChange(v === NONE ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {clearable && <SelectItem value={NONE}>—</SelectItem>}
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </Field>
  )
}

function MemberSelect({
  control,
  name,
  label,
  members,
}: {
  control: Control<ContentInput>
  name: 'assignee_id'
  label: string
  members: { user_id: string; profile: { full_name: string | null; email: string } | null }[]
}) {
  return (
    <Field label={label}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            value={(field.value as string) || NONE}
            onValueChange={(v) => field.onChange(v === NONE ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Ninguém" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Ninguém</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>
                  {m.profile?.full_name ?? m.profile?.email ?? 'Sem nome'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </Field>
  )
}

function RelSelect({
  control,
  name,
  label,
  items,
}: {
  control: Control<ContentInput>
  name: 'campaign_id' | 'event_id'
  label: string
  items: { id: string; name: string }[]
}) {
  return (
    <Field label={label}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            value={(field.value as string) || NONE}
            onValueChange={(v) => field.onChange(v === NONE ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Nenhum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Nenhum</SelectItem>
              {items.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {i.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </Field>
  )
}
